/**
 * Client-side index of the user's private tags, kept in IndexedDB.
 *
 * Why IndexedDB and not localStorage:
 *  - we want this scoped per-origin and not synced across tabs through the
 *    JS event loop;
 *  - we want async, non-blocking reads on the autocomplete hot path;
 *  - private tags must never round-trip through the server, so the storage
 *    has to be local.
 *
 * The schema is intentionally tiny: a single object store keyed by the
 * lowercase tag name, with a `count` field for ranking suggestions.
 */

const DB_NAME = 'vinterliste';
const STORE = 'private_tags';
const VERSION = 1;

interface TagRow {
  name: string;
  count: number;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'name' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => Promise<T> | T,
): Promise<T> {
  const db = await openDb();
  return await new Promise<T>((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const store = tx.objectStore(STORE);
    let result: T;
    Promise.resolve(fn(store)).then((r) => { result = r; }).catch(reject);
    tx.oncomplete = () => resolve(result);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

function normalise(name: string): string {
  return name.trim().toLowerCase();
}

export const privateTagIndex = {
  /** Bump or insert every tag in `tags`. Call after creating/updating a private activity. */
  async record(tags: string[]): Promise<void> {
    const names = [...new Set(tags.map(normalise).filter(Boolean))];
    if (names.length === 0) return;
    await withStore('readwrite', (store) => {
      for (const name of names) {
        const req = store.get(name);
        req.onsuccess = () => {
          const existing = req.result as TagRow | undefined;
          const next: TagRow = existing
            ? { name, count: existing.count + 1 }
            : { name, count: 1 };
          store.put(next);
        };
      }
    });
  },

  /** Decrement counts for the tags of an activity that's being removed/edited. */
  async release(tags: string[]): Promise<void> {
    const names = [...new Set(tags.map(normalise).filter(Boolean))];
    if (names.length === 0) return;
    await withStore('readwrite', (store) => {
      for (const name of names) {
        const req = store.get(name);
        req.onsuccess = () => {
          const existing = req.result as TagRow | undefined;
          if (!existing) return;
          if (existing.count <= 1) store.delete(name);
          else store.put({ name, count: existing.count - 1 });
        };
      }
    });
  },

  /** Return suggestions matching `q` (prefix match), sorted by count desc. */
  async suggest(q: string, limit = 20): Promise<TagRow[]> {
    const prefix = normalise(q);
    return withStore('readonly', (store) => new Promise<TagRow[]>((resolve, reject) => {
      const out: TagRow[] = [];
      const req = store.openCursor();
      req.onsuccess = () => {
        const cursor = req.result;
        if (!cursor) {
          out.sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
          resolve(out.slice(0, limit));
          return;
        }
        const row = cursor.value as TagRow;
        if (!prefix || row.name.startsWith(prefix)) out.push(row);
        cursor.continue();
      };
      req.onerror = () => reject(req.error);
    }));
  },

  /** Drop the index — used on logout to leave no per-user residue. */
  async clear(): Promise<void> {
    await withStore('readwrite', (store) => {
      store.clear();
    });
  },
};
